import React from 'react';
import { Clock, Loader2, LogIn, LogOut } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface TimeClockEntry {
    id: string;
    clock_in: string;
    clock_out: string | null;
    note: string;
}

const formatDuration = (ms: number) => {
    const totalMinutes = Math.max(0, Math.floor(ms / 60000));
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
};

const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const formatDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

export const EmployeeTimeClock: React.FC = () => {
    const [open, setOpen] = React.useState<TimeClockEntry | null>(null);
    const [history, setHistory] = React.useState<TimeClockEntry[]>([]);
    const [enabled, setEnabled] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [toggling, setToggling] = React.useState(false);
    const [now, setNow] = React.useState(() => Date.now());

    const load = () => {
        moduleApi.getMyTimeClock().then((d: any) => {
            setOpen(d?.open || null);
            setHistory(Array.isArray(d?.history) ? d.history : []);
            setEnabled(d?.enabled !== false);
        }).catch(() => {}).finally(() => setLoading(false));
    };
    React.useEffect(() => { load(); }, []);

    React.useEffect(() => {
        if (!open) return;
        const interval = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(interval);
    }, [open]);

    const handleToggle = async () => {
        setToggling(true);
        try {
            await moduleApi.toggleMyTimeClock();
            load();
        } catch {} finally {
            setToggling(false);
        }
    };

    const elapsed = open ? formatDuration(now - new Date(open.clock_in).getTime()) : null;

    return (
        <div className="space-y-6 max-w-2xl">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><Clock size={24} /> Pointage</h1>
                <p className="text-content-muted mt-1">Pointez le début et la fin de votre service.</p>
            </header>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : !enabled ? (
                <div className="p-8 rounded-2xl border border-dashed border-border text-center">
                    <p className="text-sm text-content-muted">Le pointage n'est pas activé par votre établissement pour le moment.</p>
                </div>
            ) : (
                <div className={`p-8 rounded-2xl border text-center space-y-4 ${open ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-surface-soft border-border'}`}>
                    {open ? (
                        <>
                            <p className="text-xs font-bold uppercase tracking-widest text-emerald-500">En service depuis {formatTime(open.clock_in)}</p>
                            <p className="text-4xl font-black text-content tabular-nums">{elapsed}</p>
                        </>
                    ) : (
                        <p className="text-sm text-content-muted">Vous n'êtes pas en service.</p>
                    )}
                    <button
                        onClick={handleToggle}
                        disabled={toggling}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-50 ${
                            open ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-brand-600 hover:bg-brand-700 text-brand-fg'
                        }`}
                    >
                        {toggling ? <Loader2 size={16} className="animate-spin" /> : open ? <LogOut size={16} /> : <LogIn size={16} />}
                        {open ? 'Je termine mon service' : 'Je commence mon service'}
                    </button>
                </div>
            )}

            <div>
                <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest mb-2">Historique</h2>
                {history.length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-border rounded-2xl">
                        <p className="text-content-muted text-sm">Aucun pointage pour l'instant.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {history.map((h) => (
                            <div key={h.id} className="flex items-center justify-between p-3.5 rounded-xl bg-surface-soft border border-border">
                                <div>
                                    <p className="text-sm font-semibold text-content capitalize">{formatDate(h.clock_in)}</p>
                                    <p className="text-xs text-content-muted mt-0.5">
                                        {formatTime(h.clock_in)} — {h.clock_out ? formatTime(h.clock_out) : 'en cours'}
                                    </p>
                                </div>
                                {h.clock_out && (
                                    <span className="text-xs font-bold text-content-muted tabular-nums">
                                        {formatDuration(new Date(h.clock_out).getTime() - new Date(h.clock_in).getTime())}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeTimeClock;
